import Client from "shopify-buy";

const SHOP_DOMAIN = "rosscreations-merch.myshopify.com";
const STOREFRONT_TOKEN = "0fe7810fb1cb14f71c9c4564f9a724a0";
const API_VERSION = "2024-10";

export const shopifyClient = Client.buildClient({
  domain: SHOP_DOMAIN,
  storefrontAccessToken: STOREFRONT_TOKEN,
  apiVersion: API_VERSION,
});

export const COLLECTION_HANDLES: { handle: string; title: string }[] = [
  { handle: "all-merch", title: "All Merch" },
  { handle: "rosscreations", title: "RossCreations" },
  { handle: "jacob-markovich", title: "Jacob Markovich" },
];

export type ShopVariant = {
  id: string;
  title: string;
  availableForSale: boolean;
  price: { amount: string; currencyCode: string };
};

export type ShopProduct = {
  id: string;
  handle: string;
  title: string;
  availableForSale: boolean;
  images: Array<{ src: string; altText: string | null }>;
  variants: ShopVariant[];
};

export type ShopCollection = {
  id: string;
  handle: string;
  title: string;
  products: ShopProduct[];
};

const COLLECTION_QUERY = `
  query CollectionByHandle($handle: String!) {
    collectionByHandle(handle: $handle) {
      id
      handle
      title
      products(first: 50) {
        edges {
          node {
            id
            handle
            title
            availableForSale
            images(first: 5) {
              edges { node { url altText } }
            }
            variants(first: 25) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  price { amount currencyCode }
                }
              }
            }
          }
        }
      }
    }
  }
`;

type GqlResponse = {
  data?: {
    collectionByHandle: {
      id: string;
      handle: string;
      title: string;
      products: {
        edges: Array<{
          node: {
            id: string;
            handle: string;
            title: string;
            availableForSale: boolean;
            images: { edges: Array<{ node: { url: string; altText: string | null } }> };
            variants: {
              edges: Array<{
                node: {
                  id: string;
                  title: string;
                  availableForSale: boolean;
                  price: { amount: string; currencyCode: string };
                };
              }>;
            };
          };
        }>;
      };
    } | null;
  };
  errors?: Array<{ message: string }>;
};

export async function fetchCollectionByHandle(handle: string): Promise<ShopCollection | null> {
  const res = await fetch(
    `https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: COLLECTION_QUERY, variables: { handle } }),
    },
  );

  if (!res.ok) throw new Error(`Shopify request failed: ${res.status}`);
  const json = (await res.json()) as GqlResponse;
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join(", "));
  }
  const c = json.data?.collectionByHandle;
  if (!c) return null;

  return {
    id: c.id,
    handle: c.handle,
    title: c.title,
    products: c.products.edges.map(({ node }) => ({
      id: node.id,
      handle: node.handle,
      title: node.title,
      availableForSale: node.availableForSale,
      images: node.images.edges.map(({ node: img }) => ({
        src: img.url,
        altText: img.altText,
      })),
      variants: node.variants.edges.map(({ node: v }) => ({
        id: v.id,
        title: v.title,
        availableForSale: v.availableForSale,
        price: v.price,
      })),
    })),
  };
}

export type ShopVariantDetail = ShopVariant & {
  selectedOptions: Array<{ name: string; value: string }>;
  image: { src: string; altText: string | null } | null;
  compareAtPrice: { amount: string; currencyCode: string } | null;
};

export type ShopProductDetail = Omit<ShopProduct, "variants"> & {
  description: string;
  descriptionHtml: string;
  vendor: string | null;
  productType: string | null;
  options: Array<{ id: string; name: string; values: string[] }>;
  variants: ShopVariantDetail[];
};

const PRODUCT_QUERY = `
  query ProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      handle
      title
      description
      descriptionHtml
      vendor
      productType
      availableForSale
      options { id name values }
      images(first: 15) {
        edges { node { url altText } }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            availableForSale
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            selectedOptions { name value }
            image { url altText }
          }
        }
      }
    }
  }
`;

type ProductGqlResponse = {
  data?: {
    productByHandle: {
      id: string;
      handle: string;
      title: string;
      description: string;
      descriptionHtml: string;
      vendor: string | null;
      productType: string | null;
      availableForSale: boolean;
      options: Array<{ id: string; name: string; values: string[] }>;
      images: { edges: Array<{ node: { url: string; altText: string | null } }> };
      variants: {
        edges: Array<{
          node: {
            id: string;
            title: string;
            availableForSale: boolean;
            price: { amount: string; currencyCode: string };
            compareAtPrice: { amount: string; currencyCode: string } | null;
            selectedOptions: Array<{ name: string; value: string }>;
            image: { url: string; altText: string | null } | null;
          };
        }>;
      };
    } | null;
  };
  errors?: Array<{ message: string }>;
};

export async function fetchProductByHandle(handle: string): Promise<ShopProductDetail | null> {
  const res = await fetch(
    `https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: PRODUCT_QUERY, variables: { handle } }),
    },
  );

  if (!res.ok) throw new Error(`Shopify request failed: ${res.status}`);
  const json = (await res.json()) as ProductGqlResponse;
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join(", "));
  }
  const p = json.data?.productByHandle;
  if (!p) return null;

  return {
    id: p.id,
    handle: p.handle,
    title: p.title,
    description: p.description,
    descriptionHtml: p.descriptionHtml,
    vendor: p.vendor,
    productType: p.productType,
    availableForSale: p.availableForSale,
    options: p.options,
    images: p.images.edges.map(({ node }) => ({
      src: node.url,
      altText: node.altText,
    })),
    variants: p.variants.edges.map(({ node: v }) => ({
      id: v.id,
      title: v.title,
      availableForSale: v.availableForSale,
      price: v.price,
      compareAtPrice: v.compareAtPrice,
      selectedOptions: v.selectedOptions,
      image: v.image ? { src: v.image.url, altText: v.image.altText } : null,
    })),
  };
}
